import { Heart, MessageCircle, Trash2, Pin, PinOff } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getSection } from "@/lib/sections";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const PostCard = ({ post, onUpdate, onDelete, variant = "default" }) => {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [pinnedGlobally, setPinnedGlobally] = useState(!!post.pinned_globally);
  const [pinnedOnProfile, setPinnedOnProfile] = useState(!!post.pinned_on_profile);

  const toggleLike = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      setLikesCount(data.likes_count);
      // Do NOT call onUpdate() here — that would trigger a full feed refetch
      // and cause a "flash" (loading state briefly replacing the post grid).
      // Local liked/likesCount state is sufficient for immediate feedback.
    } finally {
      setBusy(false);
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted.");
      setDeleted(true);
      if (onDelete) onDelete(post.id);
      else if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete post.");
    }
  };

  const togglePinGlobal = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/pin-global`);
      setPinnedGlobally(data.pinned_globally);
      toast.success(data.pinned_globally ? "Pinned to the top of the salon." : "Unpinned.");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not pin.");
    }
  };

  const togglePinProfile = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/pin-profile`);
      setPinnedOnProfile(data.pinned_on_profile);
      toast.success(data.pinned_on_profile ? "Pinned to your profile." : "Unpinned from your profile.");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not pin.");
    }
  };

  const author = post.author;
  const isBlog = post.post_type === "blog";
  const section = getSection(post.section);
  const isOwner = user && author && user.id === author.id;
  const canDelete = isOwner || user?.is_editor;

  if (deleted) return null;

  return (
    <article
      data-testid={`post-card-${post.id}`}
      className="card-belle card-belle-hover fade-up"
      style={{ marginBottom: 48, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}
    >
      {/* Author header */}
      <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid var(--border-default)" }}>
        <Link to={author ? `/profile/${author.username}` : "#"}>
          {author?.avatar_url ? (
            <img
              src={author.avatar_url}
              alt={author.username}
              loading="lazy"
              decoding="async"
              style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--border-default)" }}
            />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--bg-alt)" }} />
          )}
        </Link>
        <div style={{ flex: 1 }}>
          <Link
            to={author ? `/profile/${author.username}` : "#"}
            data-testid={`post-author-${post.id}`}
            className="font-serif"
            style={{ fontSize: 18, color: "var(--text-primary)", fontWeight: 500 }}
          >
            {author?.display_name || "Anonymous"}
          </Link>
          <div className="overline" style={{ marginTop: 2 }}>
            {author?.location ? `${author.location} · ` : ""}{formatDate(post.created_at)}
          </div>
        </div>
        {post.featured && (
          <Link to="/editors-letter" data-testid={`featured-badge-${post.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid var(--brand-accent)", color: "var(--brand-accent)", fontSize: 10, padding: "5px 10px", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 500, background: "rgba(232, 213, 209, 0.15)" }}>
            ★ Editor&apos;s pick
          </Link>
        )}
        {section && (
          <Link
            to={`/room/${section.slug}`}
            data-testid={`section-badge-${post.id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              border: `1px solid ${section.accent}`, color: section.accent,
              fontSize: 10, padding: "5px 10px", letterSpacing: "0.2em",
              textTransform: "uppercase", fontWeight: 500,
              background: "transparent",
            }}
          >
            {section.label}
          </Link>
        )}
        {isBlog && <span className="badge-coming" style={{ borderColor: "var(--border-default)", background: "var(--bg-alt)", color: "var(--text-secondary)" }}>Letter</span>}
        {(pinnedGlobally || pinnedOnProfile) && (
          <span
            data-testid={`pinned-badge-${post.id}`}
            title={pinnedGlobally ? "Pinned to the salon" : "Pinned to profile"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              border: "1px solid var(--brand-accent)", color: "var(--brand-accent)",
              fontSize: 10, padding: "5px 10px", letterSpacing: "0.2em",
              textTransform: "uppercase", fontWeight: 500,
              background: "rgba(232, 213, 209, 0.15)",
            }}
          >
            <Pin size={11} strokeWidth={1.8} /> Pinned
          </span>
        )}
      </div>

      {/* Media */}
      {post.media_url && (
        <Link to={`/post/${post.id}`} className="media-zoom" style={{ display: "block", overflow: "hidden" }}>
          {post.media_type === "video" ? (
            <video src={post.media_url} controls preload="metadata" style={{ width: "100%", display: "block", maxHeight: 560 }} />
          ) : (
            <img
              src={post.media_url}
              alt=""
              loading="lazy"
              decoding="async"
              style={{ width: "100%", display: "block", maxHeight: 560, objectFit: "cover" }}
            />
          )}
        </Link>
      )}

      {/* Prominent room tag under video previews so viewers always know
          which salon room the broadcast belongs to (main feed + profiles). */}
      {post.post_type === "video" && section && (
        <div style={{ padding: "12px 24px 0" }}>
          <Link
            to={`/room/${section.slug}`}
            data-testid={`video-room-tag-${post.id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
              color: section.accent, fontFamily: "'Outfit', sans-serif",
              fontWeight: 500, borderBottom: `1px solid ${section.accent}`,
              paddingBottom: 2,
            }}
          >
            → {section.label}
          </Link>
        </div>
      )}

      {/* Body */}
      <div style={{ padding: "24px 28px 28px" }}>
        {isBlog && post.title && (
          <Link to={`/post/${post.id}`}>
            <h3
              className="editorial-h1"
              style={{ fontSize: 30, margin: 0, marginBottom: 14, color: "var(--text-primary)" }}
            >
              {post.title}
            </h3>
          </Link>
        )}

        {/* Video posts (including uploaded broadcasts) — show the title as a
            smaller heading so the card isn't just a bare video preview. */}
        {!isBlog && post.post_type === "video" && post.title && (
          <Link to={`/post/${post.id}`}>
            <h4
              className="font-serif"
              style={{ fontSize: 22, margin: 0, marginBottom: 10, color: "var(--text-primary)", fontWeight: 500, lineHeight: 1.25 }}
            >
              {post.title}
            </h4>
          </Link>
        )}

        {isBlog ? (
          <p className="editorial-body" style={{ fontSize: 16 }}>
            {post.body?.slice(0, 260)}{post.body?.length > 260 ? "…" : ""}
          </p>
        ) : (
          post.caption && (
            <p className="font-serif" style={{ fontSize: 19, lineHeight: 1.55, fontStyle: "italic", color: "var(--text-primary)", fontWeight: 400, margin: 0 }}>
              {post.caption}
            </p>
          )
        )}

        {post.tags?.length > 0 && (
          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {post.tags.map((t) => (
              <span key={t} className="tag-pill">·{t}</span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: 24 }}>
          <button
            data-testid={`like-button-${post.id}`}
            onClick={toggleLike}
            disabled={!user}
            style={{
              background: "transparent", border: "none", cursor: user ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", gap: 6, padding: 0,
              color: liked ? "var(--brand-accent)" : "var(--text-secondary)",
              fontSize: 13, letterSpacing: "0.05em",
            }}
          >
            <Heart size={18} strokeWidth={1.4} fill={liked ? "var(--brand-accent)" : "none"} />
            <span>{likesCount}</span>
          </button>
          <Link
            data-testid={`comment-link-${post.id}`}
            to={`/post/${post.id}`}
            style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-secondary)", fontSize: 13 }}
          >
            <MessageCircle size={18} strokeWidth={1.4} />
            <span>{post.comments_count}</span>
          </Link>
          {isBlog && (
            <Link to={`/post/${post.id}`} style={{ marginLeft: "auto", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)" }} className="link-underline">
              Read letter →
            </Link>
          )}
          {canDelete && (
            <button
              data-testid={`delete-post-button-${post.id}`}
              onClick={deletePost}
              title={isOwner ? "Delete post" : "Remove post (editor)"}
              style={{
                marginLeft: isBlog ? 16 : "auto",
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6, padding: 0,
                color: "var(--text-muted)", fontSize: 13,
              }}
            >
              <Trash2 size={16} strokeWidth={1.4} />
            </button>
          )}
          {isOwner && (
            <button
              data-testid={`pin-profile-button-${post.id}`}
              onClick={togglePinProfile}
              title={pinnedOnProfile ? "Unpin from your profile" : "Pin to your profile"}
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6, padding: 0,
                color: pinnedOnProfile ? "var(--brand-accent)" : "var(--text-muted)", fontSize: 13,
              }}
            >
              {pinnedOnProfile ? <PinOff size={16} strokeWidth={1.4} /> : <Pin size={16} strokeWidth={1.4} />}
            </button>
          )}
          {user?.is_editor && (
            <button
              data-testid={`pin-global-button-${post.id}`}
              onClick={togglePinGlobal}
              title={pinnedGlobally ? "Unpin from the salon" : "Pin to top of the salon"}
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 4, padding: "0 6px",
                color: pinnedGlobally ? "var(--brand-accent)" : "var(--text-muted)", fontSize: 10,
                letterSpacing: "0.15em", textTransform: "uppercase",
              }}
            >
              {pinnedGlobally ? <PinOff size={14} strokeWidth={1.6} /> : <Pin size={14} strokeWidth={1.6} />}
              <span>{pinnedGlobally ? "Unpin" : "Pin"}</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
